import { API } from '@unboared/react';
import { DocumentReference, Firestore, Timestamp, addDoc, collection, deleteDoc, updateDoc } from 'firebase/firestore';
import { normalizeScreenID } from '~/utils/screenID';
import { trackLivePlayers } from '~/utils/liveGamepads';

/**
 * One ongoing "visit" of a single player: from the moment they join until they
 * leave for good. Unlike a `gamepadSession` (one doc per player+game, recreated at
 * every game switch), a visit spans the WHOLE presence of the player, games changes
 * included — that is exactly the "temps moyen par visite" metric the airport free-play
 * dashboard needs.
 */
class PlayerVisit {
    uid: string;

    /** Last known deviceID for this visit — kept to late-resolve the uid (gotcha #35). */
    deviceID: string;

    startTime: Timestamp;

    /** Last time the player was known present (connect/reconnect, or disconnect). The
        visit ends at this instant when it is finalized after a disconnect. */
    lastSeen: Timestamp;

    /** Distinct games played during the visit, in order of first appearance. */
    games: string[];

    /** Pending finalization timer (set on disconnect, cleared on reconnect within
        tolerance). Non-null = the player is currently away but may still come back. */
    pendingClose: ReturnType<typeof setTimeout> | null = null;

    /** Set once the visit doc is created (after it crosses SECONDS_BEFORE_COUNT). */
    docRef?: DocumentReference;

    /** Guards against a double create when finalize + heartbeat race before docRef is set. */
    creating: boolean = false;

    /** Set when the visit was merged into another (both-key reconnect race): its doc, if any,
        must be deleted rather than left as a second playerVisit for the same device. Handled
        by _saveVisit's finally so an in-flight create is deleted as soon as it lands. */
    discarded: boolean = false;

    constructor(uid: string, deviceID: string, startTime: Timestamp, currentGameID: string | null) {
        this.uid = uid;
        this.deviceID = deviceID;
        this.startTime = startTime;
        this.lastSeen = startTime;
        this.games = currentGameID ? [currentGameID] : [];
    }
}

/**
 * Records one `clients/{clientID}/playerVisit` document per (player, visit) covering
 * the player's full presence on a screen, across game switches.
 *
 * Mounted ONCE per screen session (NOT per game) — cf the per-game
 * `FirestoreGamepadDataCollector`, which is recreated at every switch and therefore
 * cannot measure a cross-game visit. Free-play / airports only.
 *
 * Reconnection tolerance: a phone that sleeps or drops wifi briefly must NOT split the
 * visit in two. On disconnect we don't close immediately; we schedule a finalization
 * `RECONNECT_TOLERANCE_MS` later. If the player comes back (onConnect / onDeviceBack)
 * before it fires, the same visit continues. The dead gap is excluded from the duration
 * (the visit ends at the disconnect instant, not at finalization).
 */
export class FirestorePlayerVisitDataCollector implements IDataCollector {
    /** Visits shorter than this are dropped (same floor as gamepadSession). */
    private static SECONDS_BEFORE_COUNT: number = 30;

    /** A reconnection within this window continues the same visit (cf gotcha #2/#66:
        sleep / wifi blip → the screen may see disconnect then connect). 5 min. */
    private static RECONNECT_TOLERANCE_MS: number = 5 * 60 * 1000;

    /** Upsert cadence — refreshes each present player's endTimestamp so an abrupt screen
        close (airport end-of-day power-cycle) loses at most the last interval (~3 min)
        instead of the whole open visit — and visits are biased LONG, so losing them
        systematically under-counts the dashboard's "temps moyen par visite" (gotcha #111).
        Same cadence/rationale as FirestoreGamepadDataCollector. */
    private static HEARTBEAT_MS: number = 180000;

    private _db: Firestore;

    private _clientID: string;

    private _screenID: string;

    private _liveTeardown?: () => void;

    /** deviceID → uid, captured at connect time. Needed because `API.getUID(deviceID)`
        reads the live device and returns "" once it has disconnected. */
    private _deviceUid: Map<string, string> = new Map();

    /** visit key (uid, or `dev:<deviceID>` fallback) → ongoing visit. */
    private _visits: Map<string, PlayerVisit> = new Map();

    private _currentGameID: string | null = null;

    private _heartbeatTimer?: ReturnType<typeof setInterval>;

    constructor(db: Firestore, clientID: string, screenID: string) {
        this._db = db;
        this._clientID = clientID;
        this._screenID = normalizeScreenID(screenID);
    }

    start() {
        // Shared seed/subscribe/reconcile lifecycle (cf gotcha #103/#111, same primitive as
        // FirestoreGamepadDataCollector and FirestoreScreenDataCollector, see liveGamepads.ts):
        // - onConnect / onDeviceBack: open or continue a visit (onDeviceBack = grace-period
        //   reconnection, fires instead of onConnect — gotcha #2).
        // - onDisconnect: schedule the finalization after the reconnection tolerance.
        // - onReconcileNewDevice: seed + backstop — the primitive sweeps getGamepadIDs()
        //   (at start, then periodically) and delivers each never-seen device, catching
        //   players the events missed after a screen reload (without it a whole session
        //   records ZERO visits). Its ever-seen semantics guarantee an already-delivered
        //   deviceID is never re-delivered, so a finalized visit's lingering ghost or a
        //   superseded deviceID (visit migrated to a new device) cannot be resurrected
        //   as a phantom visit — no per-collector tombstone needed.
        this._liveTeardown = trackLivePlayers({
            onConnect: (deviceID: string) => this._open(deviceID),
            onDeviceBack: (deviceID: string) => this._open(deviceID),
            onDisconnect: (deviceID: string) => this._scheduleClose(deviceID),
            onReconcileNewDevice: (deviceID: string) => this._open(deviceID),
        });

        this._heartbeatTimer = setInterval(() => this._heartbeat(), FirestorePlayerVisitDataCollector.HEARTBEAT_MS);
    }

    /** Upsert each PRESENT player's visit so progress survives an abrupt screen close.
        Players currently away (pendingClose set) are skipped: their visit already ends at
        lastSeen, and rolling their endTimestamp forward would count the dead gap. Also gated
        on live presence (like FirestoreGamepadDataCollector._heartbeat): a device that
        vanished without a clean onDisconnect — so its visit never got a pendingClose — must
        not have its endTimestamp rolled forward indefinitely. */
    private _heartbeat() {
        const liveIDs = new Set(API.getGamepadIDs());
        const now = Timestamp.now();
        this._visits.forEach((visit) => {
            if (visit.pendingClose) return;
            if (!liveIDs.has(visit.deviceID)) return;
            this._saveVisit(visit, now);
        });
    }

    /** Called by the screen whenever the active game changes, so each visit accumulates
        the list of games the player went through. */
    noteGame(gameID: string | null) {
        this._currentGameID = gameID;
        if (!gameID) return;
        this._visits.forEach((visit) => {
            // Skip players currently away (waiting out the reconnection window): they
            // are not present for this game, so don't attribute it to their visit.
            if (visit.pendingClose) return;
            if (visit.games[visit.games.length - 1] !== gameID) {
                visit.games.push(gameID);
            }
        });
    }

    private _open(deviceID: string) {
        const uid = API.getUID(deviceID);
        if (uid) this._deviceUid.set(deviceID, uid);
        const key = uid || `dev:${deviceID}`;

        // The uid can be '' right after connect — the gamepad announces before Firebase
        // anon auth resolves (gotcha #35, ~21.5% of gamepad sessions start uid-less). The
        // first _open then opens a visit under the pre-auth `dev:<deviceID>` fallback key.
        // Once the uid is known, fold that visit onto its real uid key BEFORE the lookup
        // below; otherwise the lookup misses it and opens a SECOND visit for the same
        // physical device → the player is double-counted (two playerVisit docs).
        if (uid) this._absorbPreAuthVisit(deviceID, uid);

        const existing = this._visits.get(key);
        if (existing) {
            // Reconnection within tolerance → continue the same visit.
            if (existing.pendingClose) {
                clearTimeout(existing.pendingClose);
                existing.pendingClose = null;
            }
            existing.deviceID = deviceID;
            existing.lastSeen = Timestamp.now();
            if (this._currentGameID && existing.games[existing.games.length - 1] !== this._currentGameID) {
                existing.games.push(this._currentGameID);
            }
            return;
        }
        this._visits.set(key, new PlayerVisit(uid, deviceID, Timestamp.now(), this._currentGameID));
    }

    /** Re-key a pre-auth `dev:<deviceID>` visit onto its now-known uid, carrying its
        docRef, start, games and pending timer — so a device seen uid-less then uid-ful
        keeps a SINGLE visit (gotcha #35). If a uid-keyed visit already exists for it (a
        rare reconnect where getUID flickered back to '' and opened a fresh dev: visit),
        merge the two rather than leave the pre-auth one dangling until stop(). */
    private _absorbPreAuthVisit(deviceID: string, uid: string) {
        const preKey = `dev:${deviceID}`;
        const pre = this._visits.get(preKey);
        if (!pre) return;
        this._visits.delete(preKey);
        pre.uid = uid;

        const existing = this._visits.get(uid);
        if (!existing) {
            this._visits.set(uid, pre);
            return;
        }
        // Both exist: keep the earliest start, union the games in order, and cancel the
        // pre-auth timer.
        if (pre.pendingClose) clearTimeout(pre.pendingClose);
        if (pre.startTime.seconds < existing.startTime.seconds) existing.startTime = pre.startTime;
        pre.games.forEach((g) => {
            if (!existing.games.includes(g)) existing.games.push(g);
        });
        // The pre-auth visit must not survive as a SECOND playerVisit doc for this device.
        // If the surviving visit has no doc yet, hand it the pre-auth one; otherwise delete
        // it. A create still in flight (pre.creating) has no docRef yet — the `discarded`
        // flag makes _saveVisit's finally delete it the moment it lands.
        if (!existing.docRef && pre.docRef) {
            existing.docRef = pre.docRef;
        } else {
            pre.discarded = true;
            if (pre.docRef) {
                deleteDoc(pre.docRef).catch((err) => {
                    console.error('playerVisit discard failed', err);
                });
            }
        }
    }

    private _scheduleClose(deviceID: string) {
        const uid = this._deviceUid.get(deviceID);
        const key = uid || `dev:${deviceID}`;
        const visit = this._visits.get(key);
        this._deviceUid.delete(deviceID);
        if (!visit) return;

        // Mark the away instant as the visit end, then wait out the tolerance window.
        visit.lastSeen = Timestamp.now();
        if (visit.pendingClose) clearTimeout(visit.pendingClose);
        visit.pendingClose = setTimeout(() => {
            this._finalize(key);
        }, FirestorePlayerVisitDataCollector.RECONNECT_TOLERANCE_MS);
    }

    private _finalize(key: string) {
        const visit = this._visits.get(key);
        if (!visit) return;
        if (visit.pendingClose) {
            clearTimeout(visit.pendingClose);
            visit.pendingClose = null;
        }
        this._visits.delete(key);
        this._saveVisit(visit, visit.lastSeen);
    }

    stop() {
        // Tear down reconcile interval + listeners first so nothing fires mid-flush. Without
        // unsubscribing, stale listeners keep firing after a screen teardown and leak.
        if (this._liveTeardown) {
            this._liveTeardown();
            this._liveTeardown = undefined;
        }
        if (this._heartbeatTimer) {
            clearInterval(this._heartbeatTimer);
            this._heartbeatTimer = undefined;
        }
        // Finalize every ongoing visit. A player still connected ends now; a player
        // already away (pendingClose set) ends at their disconnect instant (lastSeen).
        const now = Timestamp.now();
        this._visits.forEach((visit) => {
            if (visit.pendingClose) clearTimeout(visit.pendingClose);
            this._saveVisit(visit, visit.pendingClose ? visit.lastSeen : now);
        });
        this._visits.clear();
        this._deviceUid.clear();
    }

    /** Create the visit doc once it crosses the min duration, then keep its endTimestamp
        (and games list, which grows during the visit) fresh on later calls — heartbeat and
        finalization both land here. Fire-and-forget (callers run in sync teardown paths),
        idempotent per visit; errors are swallowed so a failed write — flaky wifi, missing
        rule — doesn't raise an unhandled rejection. */
    private async _saveVisit(visit: PlayerVisit, endTimestamp: Timestamp) {
        if (visit.creating) return;
        const duration = endTimestamp.seconds - visit.startTime.seconds;
        if (duration <= FirestorePlayerVisitDataCollector.SECONDS_BEFORE_COUNT) return;
        // Late-resolved uid: getUID can return '' right after connect; backfill once known
        // (and keep it in the update payload so an already-created doc gets repaired) so
        // the visit is not silently dropped from distinct-player counts (gotcha #35).
        if (!visit.uid) {
            // eslint-disable-next-line no-param-reassign
            visit.uid = API.getUID(visit.deviceID) || visit.uid;
        }
        const progress = {
            endTimestamp,
            uid: visit.uid,
            gamesPlayed: visit.games,
            numGames: visit.games.length,
        };
        if (visit.docRef) {
            updateDoc(visit.docRef, progress).catch((err) => {
                console.error('playerVisit update failed', err);
            });
            return;
        }
        // eslint-disable-next-line no-param-reassign
        visit.creating = true;
        try {
            // eslint-disable-next-line no-param-reassign
            visit.docRef = await addDoc(this._collectionRef(), {
                startTimestamp: visit.startTime,
                screenID: this._screenID,
                ...progress,
            });
        } catch (err) {
            console.error('playerVisit save failed', err);
        } finally {
            // eslint-disable-next-line no-param-reassign
            visit.creating = false;
            // Merged away while the create was in flight → delete the now-orphaned doc so it
            // doesn't linger as a second playerVisit for the same device.
            if (visit.discarded && visit.docRef) {
                deleteDoc(visit.docRef).catch((err) => {
                    console.error('playerVisit discard failed', err);
                });
            }
        }
    }

    private _collectionRef() {
        return collection(this._db, 'clients', this._clientID, 'playerVisit');
    }
}
